<script>
export default {
  inheritAttrs: false,
};
</script>
<script setup>
import { defineEmits, defineExpose, defineProps, ref, toRefs } from 'vue';
import { useAriaDescribedBy, useFieldId, useGuid, useLoadingCheck } from '@/app/composables';

const props = defineProps({
  hasError: {
    default: false,
    required: false,
    type: Boolean,
  },
  legendErrorId: {
    default: '',
    required: false,
    type: String,
  },
  modelValue: {
    default: '',
    required: true,
    type: [String, Number],
  },
  name: {
    required: false,
    type: String,
  },
  placeholder: {
    default: '',
    required: false,
    type: String,
  },
  type: {
    default: 'text',
    required: false,
    type: String,
  },
});
const id = useGuid();
const field = ref(null);
const { errorsId, inputId } = useFieldId(id);
const { hasError } = toRefs(props);
const { ariaDescribedBy } = useAriaDescribedBy(hasError, errorsId);
const { loadingPrevent, loadingTabindex } = useLoadingCheck();
const emit = defineEmits(['update:modelValue']);

function inputHandler(event) {
  emit('update:modelValue', event.target.value);
}

defineExpose({
  field,
});
</script>

<template>
  <div class="c-field">
    <label :for="inputId" class="c-field__label">
      <slot name="label" />
    </label>
    <div :id="errorsId" aria-live="assertive" class="c-field__error" v-if="hasError">
      <slot name="error" />
    </div>
    <div class="c-field__input-wrapper">
      <input
        v-bind="$attrs"
        @keydown="loadingPrevent"
        @input="inputHandler"
        :aria-describedby="legendErrorId || ariaDescribedBy"
        :name="name"
        :id="inputId"
        :placeholder="placeholder"
        :tabindex="loadingTabindex"
        :type="type"
        :value="modelValue"
        class="c-field__input"
        ref="field"
      />
      <slot name="input-cta" />
    </div>
  </div>
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/forms';

.c-field {
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 0;
}

.c-field__label {
  @include label-style;
}

.c-field__error {
  @include field-error;
}

.c-field__input {
  @include general-input;
}
</style>
